/**
 * GamingCenterPage.js - Módulo de Gaming Center
 * CG Business ERP
 * 
 * Gestão de sessões de jogos:
 * - FIFA (3min, 5min, 7min, 10min)
 * - PlayStation Diversos
 * - Pandora
 * - Controlo de tempo em tempo real
 * - Consolas configuráveis (8 por defeito)
 */

import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { ModulePageHero } from '@/components/common/ModulePageHero';
import { EmptyStatePanel } from '@/components/common/EmptyStatePanel';
import { ModuleStateBoundary } from '@/components/common/ModuleStateBoundary';
import { 
  Gamepad2, Play, Square, Clock, Timer, DollarSign, Users,
  Monitor, Plus, Minus, CircleDot, AlertCircle, CheckCircle,
  CreditCard, Banknote, Smartphone, RefreshCw, Settings, Edit, Save,
  ChevronDown
} from 'lucide-react';
import { formatCurrency, cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { settingsAPI, clientsAPI, printAPI } from '@/lib/api';
import { getStoredAuthToken } from '@/lib/authStorage';
import { useLanguage } from '@/i18n';
import { useFetchTelemetry } from '@/hooks/useFetchTelemetry';
import { safeRequest } from '@/lib/safeRequest';
import { CompactPeriodFilters } from '@/components/common/CompactPeriodFilters';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: API_BASE_URL });
api.interceptors.request.use(config => {
  const token = getStoredAuthToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Default console count
const DEFAULT_CONSOLE_COUNT = 8;

// Main payment methods (buttons)
const MAIN_PAYMENT_METHODS = ['Numerário', 'M-Pesa', 'E-MOLA'];

const GAMING_PERIOD_LABELS = {
  today: 'Hoje',
  this_week: 'Esta semana',
  this_month: 'Este mês',
  all: 'Todo período',
  custom: 'Personalizado',
};

const formatDuration = (totalSeconds = 0) => {
  const seconds = Math.max(Number(totalSeconds || 0), 0);
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return 'N/A';
  return new Intl.DateTimeFormat('pt-MZ', {
    timeZone: 'Africa/Maputo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(dt).replace(',', '');
};

// Gaming API
const gamingAPI = {
  getServices: () => api.get('/api/gaming/services'),
  getConsolesStatus: (locationId, count) => api.get('/api/gaming/consoles/status', { 
    params: { location_id: locationId, console_count: count } 
  }),
  getSessions: (params) => api.get('/api/gaming/sessions', { params }),
  createSession: (data) => api.post('/api/gaming/sessions', data),
  completeSession: (id) => api.post(`/api/gaming/sessions/${id}/complete`),
  extendSession: (id, extraMinutes, extraCost) => api.post(`/api/gaming/sessions/${id}/extend`, null, { 
    params: { extra_minutes: extraMinutes, extra_cost: extraCost } 
  }),
  getSummary: (params) => api.get('/api/gaming/statistics/summary', { params }),
  // Console management
  updateConsole: (id, data) => api.put(`/api/consoles/${id}`, data),
  setConsoleCount: (locationId, count) => api.put(`/api/gaming/consoles/count`, null, {
    params: { location_id: locationId, count }
  }),
};

export default function GamingCenterPage() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  // State
  const [services, setServices] = useState([]);
  const [consoles, setConsoles] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [locations, setLocations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({ total_sessions: 0, total_revenue: 0, active_sessions: 0 });
  const [sessionFilters, setSessionFilters] = useState({ period: 'today', startDate: '', endDate: '' });
  const [periodFiltersOpen, setPeriodFiltersOpen] = useState(false);
  
  // Console configuration
  const [consoleCount, setConsoleCount] = useState(DEFAULT_CONSOLE_COUNT);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [tempConsoleCount, setTempConsoleCount] = useState(DEFAULT_CONSOLE_COUNT);
  
  // Selected location
  const [selectedLocation, setSelectedLocation] = useState(null);
  const loadDataRef = useRef(null);
  const loadConsolesRef = useRef(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  
  // New Session Dialog
  const [newSessionOpen, setNewSessionOpen] = useState(false);
  const [latestCreatedSession, setLatestCreatedSession] = useState(null);
  const [selectedConsole, setSelectedConsole] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [useDefaultClientFlow, setUseDefaultClientFlow] = useState(false);
  const [quickClientOpen, setQuickClientOpen] = useState(false);
  const [creatingQuickClient, setCreatingQuickClient] = useState(false);
  const [quickClientForm, setQuickClientForm] = useState({
    name: '',
    phone: '',
    nuit: '',
    address: ''
  });
  const [quantity, setQuantity] = useState(1);
  const [selectedPaymentAccountId, setSelectedPaymentAccountId] = useState('');
  const [otherPaymentMethod, setOtherPaymentMethod] = useState('');
  const [registeredAt, setRegisteredAt] = useState('');
  const [processing, setProcessing] = useState(false);
  
  // Console Edit Dialog
  const [editConsoleOpen, setEditConsoleOpen] = useState(false);
  const [editingConsole, setEditingConsole] = useState(null);
  const [consoleName, setConsoleName] = useState('');
  const [consoleDescription, setConsoleDescription] = useState('');
  const [consoleType, setConsoleType] = useState('playstation');
  
  const { user } = useAuth();
  const { partialIssues, captureFetchTelemetry, resetFetchTelemetry } = useFetchTelemetry();

  const selectedClient = useMemo(
    () => clients.find((client) => client.id === selectedClientId) || null,
    [clients, selectedClientId]
  );

  const filteredClients = useMemo(() => {
    const q = clientSearch.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((client) =>
      (client.name || '').toLowerCase().includes(q) ||
      (client.phone || '').toLowerCase().includes(q) ||
      (client.code || client.client_code || '').toLowerCase().includes(q)
    );
  }, [clients, clientSearch]);

  const selectedPaymentAccount = useMemo(
    () => paymentAccounts.find((account) => account.id === selectedPaymentAccountId) || null,
    [paymentAccounts, selectedPaymentAccountId]
  );

  const activePeriodLabel = useMemo(() => {
    if (sessionFilters.period === 'custom' && sessionFilters.startDate && sessionFilters.endDate) {
      return `${sessionFilters.startDate} → ${sessionFilters.endDate}`;
    }
    return GAMING_PERIOD_LABELS[sessionFilters.period] || 'Período';
  }, [sessionFilters.endDate, sessionFilters.period, sessionFilters.startDate]);

  // Separate payment methods into main (buttons) and others (dropdown)
  const mainPayments = useMemo(() => {
    return paymentAccounts.filter(pm => {
      const methodLabel = pm.payment_method_name || pm.name || '';
      return MAIN_PAYMENT_METHODS.some(name => methodLabel.toLowerCase().includes(name.toLowerCase()));
    }
    );
  }, [paymentAccounts]);

  const otherPayments = useMemo(() => {
    return paymentAccounts.filter(pm => {
      const methodLabel = pm.payment_method_name || pm.name || '';
      return !MAIN_PAYMENT_METHODS.some(name => methodLabel.toLowerCase().includes(name.toLowerCase()));
    }
    );
  }, [paymentAccounts]);

  // Auto-refresh consoles every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (selectedLocation) {
        loadConsolesRef.current?.(selectedLocation.id);
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [selectedLocation, consoleCount, sessionFilters]);

  // Load initial data
  useEffect(() => {
    loadDataRef.current?.();
  }, []);

  // Load consoles when location changes
  useEffect(() => {
    if (selectedLocation) {
      loadConsolesRef.current?.(selectedLocation.id);
    }
  }, [selectedLocation, consoleCount, sessionFilters]);

  const loadData = async () => {
    try {
      setLoading(true);
      resetFetchTelemetry();
      const [servicesRes, locationsRes, employeesRes, paymentRes, clientsRes] = await Promise.all([
        safeRequest(() => gamingAPI.getServices(), { data: [] }, { telemetry: { module: 'gaming-center', endpoint: 'gaming.services', onEvent: captureFetchTelemetry } }),
        safeRequest(() => settingsAPI.listActiveLocations(), { data: [] }, { telemetry: { module: 'gaming-center', endpoint: 'settings.active-locations', onEvent: captureFetchTelemetry } }),
        safeRequest(() => settingsAPI.listActiveEmployees(), { data: [] }, { telemetry: { module: 'gaming-center', endpoint: 'settings.active-employees', onEvent: captureFetchTelemetry } }),
        safeRequest(() => settingsAPI.listActivePaymentAccounts(), { data: [] }, { telemetry: { module: 'gaming-center', endpoint: 'settings.active-payment-accounts', onEvent: captureFetchTelemetry } }),
        safeRequest(() => clientsAPI.listActive(), { data: [] }, { telemetry: { module: 'gaming-center', endpoint: 'clients.active', onEvent: captureFetchTelemetry } })
      ]);
      
      setServices(servicesRes.data || []);
      setLocations(locationsRes.data || []);
      setEmployees(employeesRes.data || []);
      setPaymentAccounts(paymentRes.data || []);
      setClients(clientsRes.data || []);

      // Default employee from current session (editable)
      const fallbackEmployee = (employeesRes.data || []).find((emp) =>
        emp.id === user?.id || (emp.email && user?.email && emp.email.toLowerCase() === user.email.toLowerCase())
      ) || (employeesRes.data || [])[0];
      if (fallbackEmployee) {
        setSelectedEmployee(fallbackEmployee.id);
      }

      // Default payment account from ERP settings
      const defaultAccount = (paymentRes.data || [])[0];
      if (defaultAccount) {
        setSelectedPaymentAccountId(defaultAccount.id);
      }
      
      // Auto-select first store location
      const storeLocation = locationsRes.data?.find(l => l.type === 'store');
      if (storeLocation) {
        setSelectedLocation(storeLocation);
      }
      
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error(t('gamingCenter.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const loadConsoles = async (locationId) => {
    try {
      const queryParams = {
        location_id: locationId,
        period: sessionFilters.period,
        ...(sessionFilters.startDate ? { start_date: sessionFilters.startDate } : {}),
        ...(sessionFilters.endDate ? { end_date: sessionFilters.endDate } : {}),
      };
      const [consolesRes, sessionsRes, summaryRes] = await Promise.all([
        safeRequest(() => gamingAPI.getConsolesStatus(locationId, consoleCount), { data: [] }, { telemetry: { module: 'gaming-center', endpoint: 'gaming.consoles-status', onEvent: captureFetchTelemetry } }),
        safeRequest(() => gamingAPI.getSessions(queryParams), { data: [] }, { telemetry: { module: 'gaming-center', endpoint: 'gaming.sessions', onEvent: captureFetchTelemetry } }),
        safeRequest(() => gamingAPI.getSummary(queryParams), { data: { total_sessions: 0, total_revenue: 0, active_sessions: 0 } }, { telemetry: { module: 'gaming-center', endpoint: 'gaming.summary', onEvent: captureFetchTelemetry } })
      ]);
      
      setConsoles(consolesRes.data || []);
      setSessions(sessionsRes.data || []);
      setSummary(summaryRes.data || { total_sessions: 0, total_revenue: 0, active_sessions: 0 });
      if ((consolesRes.status === 'fallback' || sessionsRes.status === 'fallback' || summaryRes.status === 'fallback')) {
        toast.info(t('gamingCenter.partialConsolesError'));
      }
    } catch (error) {
      console.error('Error loading consoles:', error);
    }
  };

  loadDataRef.current = loadData;
  loadConsolesRef.current = loadConsoles;

  // Handle console count change
  const handleSaveConsoleCount = async () => {
    try {
      setConsoleCount(tempConsoleCount);
      setConfigDialogOpen(false);
      toast.success(`Configurado para ${tempConsoleCount} consolas`);
      // Reload consoles with new count
      if (selectedLocation) {
        await loadConsoles(selectedLocation.id);
      }
    } catch (error) {
      toast.error('Erro ao configurar consolas');
    }
  };

  // Open new session dialog
  const handleOpenNewSession = (consoleNum) => {
    setSelectedConsole(consoleNum);
    setSelectedService(null);
    setSelectedClientId('');
    setClientSearch('');
    setUseDefaultClientFlow(false);
    setQuickClientOpen(false);
    setQuickClientForm({ name: '', phone: '', nuit: '', address: '' });
    setQuantity(1);
    setSelectedPaymentAccountId(paymentAccounts[0]?.id || '');
    setOtherPaymentMethod('');
    setRegisteredAt('');
    setNewSessionOpen(true);
  };

  // Calculate session total
  const sessionTotal = useMemo(() => {
    if (!selectedService) return 0;
    return (selectedService.unit_price || 0) * quantity;
  }, [selectedService, quantity]);

  const handleSelectClient = (clientId) => {
    setSelectedClientId(clientId);
    const selected = clients.find((client) => client.id === clientId);
    const code = selected?.code || selected?.client_code;
    setUseDefaultClientFlow(code === 'CL-000000');
  };

  const handleUseDefaultClient = async () => {
    try {
      const response = await clientsAPI.getDefault();
      const defaultClient = response.data;
      setClients((prev) => {
        if (prev.some((client) => client.id === defaultClient.id)) return prev;
        return [defaultClient, ...prev];
      });
      setSelectedClientId(defaultClient.id);
      setUseDefaultClientFlow(true);
      toast.success('Cliente Indiferenciado selecionado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao carregar Cliente Indiferenciado');
    }
  };

  const handleQuickCreateClient = async () => {
    if (!quickClientForm.name.trim() || !quickClientForm.phone.trim()) {
      toast.error('Nome e telefone são obrigatórios');
      return;
    }

    setCreatingQuickClient(true);
    try {
      const payload = {
        name: quickClientForm.name.trim(),
        phone: quickClientForm.phone.trim(),
        nuit: quickClientForm.nuit.trim() || null,
        address: quickClientForm.address.trim() || null,
      };
      const response = await clientsAPI.create(payload);
      const createdClient = response.data;
      setClients((prev) => [createdClient, ...prev]);
      setSelectedClientId(createdClient.id);
      setUseDefaultClientFlow(false);
      setQuickClientOpen(false);
      setQuickClientForm({ name: '', phone: '', nuit: '', address: '' });
      toast.success('Cliente criado e selecionado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar cliente');
    } finally {
      setCreatingQuickClient(false);
    }
  };

  // Create session
  const handleCreateSession = async () => {
    if (!selectedService) {
      toast.error('Seleccione um jogo/serviço');
      return;
    }
    if (!selectedEmployee) {
      toast.error('Seleccione um funcionário');
      return;
    }
    if (!selectedClientId) {
      toast.error('Seleccione um cliente');
      return;
    }
    if (!selectedPaymentAccount) {
      toast.error('Seleccione um método de pagamento');
      return;
    }

    if (registeredAt) {
      const registeredDate = new Date(registeredAt);
      const nowDate = new Date();
      const tenSecondsBeforeNow = new Date(nowDate.getTime() - 10000);

      if (registeredDate > nowDate) {
        toast.error('Data/hora de início não pode ser no futuro');
        return;
      }

      if (registeredDate < tenSecondsBeforeNow) {
        toast.error('Start time inválido: não pode ser superior a 10 segundos antes do relógio do sistema (Africa/Maputo)');
        return;
      }
    }
    
    setProcessing(true);
    try {
      // Extract duration from service unit (e.g., "3 minutos" -> 3)
      const durationMatch = selectedService.unit?.match(/(\d+)/);
      const duration = durationMatch ? parseInt(durationMatch[1]) : 10;
      
      const response = await gamingAPI.createSession({
        service_id: selectedService.id,
        service_name: selectedService.name,
        console_number: selectedConsole,
        client_id: selectedClientId,
        use_default_client: useDefaultClientFlow,
        location_id: selectedLocation.id,
        seller_id: selectedEmployee,
        employee_id: selectedEmployee,
        payment_account_id: selectedPaymentAccount.id,
        payment_method_id: selectedPaymentAccount.payment_method_id,
        quantity: quantity,
        unit_price: selectedService.unit_price || 0,
        duration_minutes: duration,
        registered_at: registeredAt ? new Date(registeredAt).toISOString() : null
      });
      
      toast.success(`Sessão iniciada! Consola ${selectedConsole} - ${selectedService.name}`);
      setLatestCreatedSession(response?.data || null);
      setNewSessionOpen(false);
      loadConsoles(selectedLocation.id);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao iniciar sessão');
    } finally {
      setProcessing(false);
    }
  };

  const handlePrintGamingPOS = async (copyType = 'Original') => {
    if (!latestCreatedSession?.sale_id) {
      toast.error('Nenhuma sessão recente para imprimir');
      return;
    }

    const result = await printAPI.renderDocumentPdf({
      doc_type: 'pos_gaming',
      sale_id: latestCreatedSession.sale_id,
      location_id: latestCreatedSession.location_id,
      copy_type: copyType,
    }, 'preview');

    if (!result?.success) {
      toast.error(result?.error || 'Erro ao imprimir POS Gaming');
      return;
    }

    toast.success(`POS Gaming ${copyType} pronto`);
  };

  // Complete session
  const handleCompleteSession = async (sessionId) => {
    try {
      await gamingAPI.completeSession(sessionId);
      toast.success('Sessão finalizada!');
      loadConsoles(selectedLocation.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao finalizar sessão');
    }
  };

  // Open edit console dialog
  const handleEditConsole = (console, e) => {
    e?.stopPropagation();
    setEditingConsole(console);
    setConsoleName(console.console_name || `Consola ${console.console_number}`);
    setConsoleDescription(console.description || '');
    setConsoleType(console.console_type || 'playstation');
    setEditConsoleOpen(true);
  };

  // Save console changes
  const handleSaveConsole = async () => {
    if (!editingConsole) return;
    
    try {
      // Find the console ID from the full consoles list
      const consoleData = consoles.find(c => c.console_number === editingConsole.console_number);
      if (!consoleData?.id) {
        // Need to get console ID from backend
        const response = await api.get('/api/consoles', { 
          params: { location_id: selectedLocation.id } 
        });
        const fullConsole = response.data.find(c => c.console_number === editingConsole.console_number);
        if (!fullConsole?.id) {
          toast.error('Consola não encontrada');
          return;
        }
        await gamingAPI.updateConsole(fullConsole.id, {
          name: consoleName,
          description: consoleDescription || null,
          console_type: consoleType
        });
      } else {
        await gamingAPI.updateConsole(consoleData.id, {
          name: consoleName,
          description: consoleDescription || null,
          console_type: consoleType
        });
      }
      
      toast.success('Consola actualizada!');
      setEditConsoleOpen(false);
      loadConsoles(selectedLocation.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao actualizar consola');
    }
  };

  // Get console status color
  const getConsoleColor = (console) => {
    if (console.is_busy) {
      if (console.time_remaining <= 2) return 'bg-red-500';
      if (console.time_remaining <= 5) return 'bg-amber-500';
      return 'bg-green-500';
    }
    return 'bg-slate-300';
  };

  const hasGamingData = Boolean(services.length || consoles.length || sessions.length || locations.length || employees.length || paymentAccounts.length || clients.length || summary?.total_sessions || summary?.active_sessions);

  return (
    <ModuleStateBoundary
      loading={loading}
      hasData={hasGamingData}
      partialIssues={partialIssues}
      loadingTestId="gaming-center-loading-state"
      partialIssuesTestId="gaming-center-partial-issues"
      emptyState={
        <div className="p-6 space-y-6" data-testid="gaming-center-page">
          <EmptyStatePanel
            title={t('gamingCenter.emptyTitle')}
            description={t('gamingCenter.emptyDescription')}
            actionLabel={t('gamingCenter.reload')}
            onAction={loadData}
            testId="gaming-center-empty-state"
          />
        </div>
      }
    >
    <div className="p-4 space-y-4" data-testid="gaming-center-page">
      <Card className="border-slate-200 shadow-sm" data-testid="gaming-operations-strip">
        <CardContent className="p-2.5">
          <div className="flex flex-wrap items-center gap-2">
            <div className="w-[190px] min-w-[190px]">
              <Select value={sessionFilters.period} onValueChange={(value) => setSessionFilters((current) => ({ ...current, period: value, startDate: '', endDate: '' }))}>
                <SelectTrigger className="h-8 rounded-full border-slate-200 bg-white text-[11px]" data-testid="gaming-period-select-inline">
                  <SelectValue placeholder={`Período: ${activePeriodLabel}`} />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(GAMING_PERIOD_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-8 rounded-full px-3 text-[11px]"
              onClick={() => {
                setTempConsoleCount(consoleCount);
                setConfigDialogOpen(true);
              }}
              data-testid="gaming-center-configure-button"
            >
              <Settings className="mr-1.5 h-3.5 w-3.5" />
              Consolas ({consoleCount})
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8 rounded-full px-3 text-[11px]"
              onClick={() => loadConsoles(selectedLocation?.id)}
              data-testid="gaming-center-refresh-button"
            >
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
              {t('gamingCenter.refresh')}
            </Button>
            <div className="flex flex-wrap gap-2" data-testid="gaming-operations-metrics">
              <Badge variant="outline" className="rounded-full border-slate-200 bg-slate-50 text-[10px] text-slate-700">
                <Play className="mr-1 h-3 w-3" /> {summary.active_sessions || 0} activas
              </Badge>
              <Badge variant="outline" className="rounded-full border-slate-200 bg-slate-50 text-[10px] text-slate-700">
                <Gamepad2 className="mr-1 h-3 w-3" /> {summary.total_sessions || 0} sessões
              </Badge>
              <Badge variant="outline" className="rounded-full border-slate-200 bg-slate-50 text-[10px] text-slate-700">
                <DollarSign className="mr-1 h-3 w-3" /> {formatCurrency(summary.total_revenue || 0)}
              </Badge>
            </div>
            <div className="w-[210px] min-w-[210px]">
              <Select value={selectedLocation?.id || ''} onValueChange={(value) => setSelectedLocation(locations.find((location) => location.id === value))}>
                <SelectTrigger className="h-8 rounded-full border-slate-200 bg-white text-[11px]" data-testid="gaming-location-select">
                  <SelectValue placeholder={t('gamingCenter.selectPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {locations.filter((location) => location.type === 'store').map((location) => (
                    <SelectItem key={location.id} value={location.id}>{location.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-[210px] min-w-[210px]">
              <Select value={selectedEmployee || ''} onValueChange={setSelectedEmployee}>
                <SelectTrigger className="h-8 rounded-full border-slate-200 bg-white text-[11px]" data-testid="gaming-employee-select">
                  <SelectValue placeholder={t('gamingCenter.selectPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.employee_number ? `${emp.employee_number} · ${emp.name}` : emp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <ModulePageHero
        testId="gaming-center-hero"
        title={t('gamingCenter.title')}
        description=""
        icon={Gamepad2}
        badge={t('gamingCenter.badge')}
        compact
        stats={[
          { label: t('gamingCenter.activeSessions'), value: summary.active_sessions || 0, helper: t('gamingCenter.activeNow') },
          { label: t('gamingCenter.sessionsToday'), value: summary.total_sessions_today || 0, helper: t('gamingCenter.dailyService') },
          { label: t('gamingCenter.revenueToday'), value: formatCurrency(summary.total_revenue_today || 0), helper: t('gamingCenter.operatingRevenue') },
          { label: t('gamingCenter.freeConsoles'), value: Math.max((consoleCount || 0) - (summary.active_sessions || 0), 0), helper: t('gamingCenter.configuredCount', { count: consoleCount || 0 }) },
        ]}
      />

      {latestCreatedSession && (
        <Card className="border-emerald-300 bg-emerald-50" data-testid="gaming-latest-session-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center justify-between">
              <span>{t('gamingCenter.latestSession')}: {latestCreatedSession.session_number}</span>
              <span className="text-emerald-700">{formatCurrency(latestCreatedSession.total || 0)}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-xs">
              <p data-testid="gaming-session-start-at"><strong>{t('gamingCenter.start')}:</strong> {formatDateTime(latestCreatedSession.start_at || latestCreatedSession.started_at)}</p>
              <p data-testid="gaming-session-end-at"><strong>{t('gamingCenter.end')}:</strong> {formatDateTime(latestCreatedSession.end_at || latestCreatedSession.ends_at)}</p>
              <p data-testid="gaming-session-duration"><strong>{t('gamingCenter.duration')}:</strong> {formatDuration(latestCreatedSession.duration_seconds || ((latestCreatedSession.duration_minutes || 0) * 60))}</p>
              <p data-testid="gaming-session-payment-label"><strong>{t('gamingCenter.paymentMethod')}:</strong> {latestCreatedSession.payment_method_name}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => handlePrintGamingPOS('Original')} data-testid="gaming-print-pos-original-button">
                {t('gamingCenter.printOriginal')}
              </Button>
              <Button size="sm" variant="outline" onClick={() => handlePrintGamingPOS('Duplicado')} data-testid="gaming-print-pos-duplicate-button">
                {t('gamingCenter.printDuplicate')}
              </Button>
              {latestCreatedSession.sale_id && (
                <Button size="sm" variant="ghost" onClick={() => navigate(`/sales?sale_id=${latestCreatedSession.sale_id}`)} data-testid="gaming-open-sale-button">
                  {t('gamingCenter.viewInSales')}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Console Grid - All in one row, auto-adjusting up to 12 */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="px-3 py-2.5">
          <CardTitle className="flex items-center gap-2 text-sm font-semibold">
            <Monitor className="h-5 w-5" />
            {t('gamingCenter.consolesOccupancy', { busy: consoles.filter(c => c.is_busy).length, total: consoles.length })}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-3 pt-0">
          <div 
            className="grid gap-2 w-full"
            style={{
              gridTemplateColumns: `repeat(${Math.min(consoleCount, 12)}, minmax(0, 1fr))`,
              gap: consoleCount >= 10 ? '0.375rem' : '0.5rem'
            }}
          >
            {consoles.slice(0, consoleCount).map(console => (
              <Card 
                key={console.console_number}
                className={cn(
                  "cursor-pointer transition-all hover:shadow-md relative group min-w-0",
                  console.is_busy ? "border-2 border-green-400" : "border-dashed border-2 hover:border-purple-400"
                )}
                onClick={() => !console.is_busy && handleOpenNewSession(console.console_number)}
              >
                {/* Edit button - appears on hover */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-0.5 right-0.5 h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                  onClick={(e) => handleEditConsole(console, e)}
                >
                  <Edit className="h-3 w-3" />
                </Button>
                
                <CardContent className={cn(
                  "text-center",
                  consoleCount >= 10 ? "p-2" : "p-3"
                )}>
                  {/* Console Number with Status Indicator */}
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <div className={cn(
                      "rounded-full animate-pulse",
                      consoleCount >= 10 ? "w-2 h-2" : "w-3 h-3",
                      getConsoleColor(console)
                    )} />
                    <span className={cn(
                      "font-bold",
                      consoleCount >= 10 ? "text-lg" : "text-2xl"
                    )}>#{console.console_number}</span>
                  </div>
                  
                  {/* Console Name - hide when too many consoles */}
                  {consoleCount < 10 && (
                    <p className="text-xs text-muted-foreground mb-1 truncate">
                      {console.console_name || t('gamingCenter.consoleLabel', { number: console.console_number })}
                    </p>
                  )}
                  
                  {console.is_busy ? (
                    <div className="space-y-1">
                      <Badge className={cn(
                        "bg-green-100 text-green-700 border-green-300",
                        consoleCount >= 10 ? "text-[10px] px-1 py-0" : ""
                      )}>
                        {t('gamingCenter.inUse')}
                      </Badge>
                      {consoleCount < 10 && (
                        <>
                          <p className="text-xs font-medium truncate">
                            {console.current_session?.service_name}
                          </p>
                          {console.current_session?.customer_name && (
                            <p className="text-xs text-muted-foreground truncate">
                              {console.current_session.customer_name}
                            </p>
                          )}
                        </>
                      )}
                      <div className="flex items-center justify-center gap-0.5 text-sm">
                        <Timer className={cn(
                          consoleCount >= 10 ? "h-3 w-3" : "h-4 w-4",
                          console.time_remaining <= 2 ? "text-red-500" : 
                          console.time_remaining <= 5 ? "text-amber-500" : "text-green-500"
                        )} />
                        <span className={cn(
                          "font-bold",
                          consoleCount >= 10 ? "text-xs" : "text-sm",
                          console.time_remaining <= 2 ? "text-red-500" : 
                          console.time_remaining <= 5 ? "text-amber-500" : "text-green-600"
                        )}>
                          {console.time_remaining}m
                        </span>
                      </div>
                      <Progress 
                        value={Math.max(0, (console.time_remaining / 10) * 100)} 
                        className="h-1"
                      />
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className={cn(
                          "w-full mt-1",
                          consoleCount >= 10 ? "h-6 text-[10px] px-1" : ""
                        )}
                        onClick={(e) => {
                          e.stopPropagation();
                          // Find the session and complete it
                          const session = sessions.find(s => 
                            s.console_number === console.console_number && s.status === 'active'
                          );
                          if (session) handleCompleteSession(session.id);
                        }}
                      >
                        <Square className="h-3 w-3 mr-1" />
                        {t('gamingCenter.stop')}
                      </Button>
                    </div>
                  ) : (
                    <div className={cn(
                      consoleCount >= 10 ? "space-y-0.5" : "space-y-1"
                    )}>
                      <Badge variant="outline" className={cn(
                        "text-slate-500",
                        consoleCount >= 10 ? "text-[10px] px-1 py-0" : ""
                      )}>
                        {t('gamingCenter.available')}
                      </Badge>
                      {consoleCount < 10 && (
                        <p className="text-xs text-muted-foreground">
                          {t('gamingCenter.clickToStart')}
                        </p>
                      )}
                      <Button 
                        size="sm" 
                        className={cn(
                          "w-full bg-purple-600 hover:bg-purple-700",
                          consoleCount >= 10 ? "h-6 text-[10px] px-1" : ""
                        )}
                        data-testid={`gaming-open-session-button-${console.console_number}`}
                      >
                        <Play className={cn(
                          "mr-0.5",
                          consoleCount >= 10 ? "h-2.5 w-2.5" : "h-3 w-3"
                        )} />
                        {consoleCount >= 10 ? t('gamingCenter.startSession') : t('gamingCenter.startSession')}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Recent Sessions */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="px-3 py-2.5">
          <CardTitle className="text-sm font-semibold">{t('gamingCenter.recentSessions')}</CardTitle>
        </CardHeader>
        <CardContent className="p-3 pt-0">
          {sessions.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              {t('gamingCenter.noSessionsToday')}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[11px] leading-tight">
                <thead className="border-b bg-slate-50/50">
                  <tr>
                    <th className="px-2 py-1.5 text-left text-[10px] font-medium" data-testid="gaming-today-header-session-number">Nº</th>
                    <th className="px-2 py-1.5 text-left text-[10px] font-medium" data-testid="gaming-today-header-start-at">Início</th>
                    <th className="px-2 py-1.5 text-left text-[10px] font-medium" data-testid="gaming-today-header-end-at">Fim</th>
                    <th className="px-2 py-1.5 text-left text-[10px] font-medium" data-testid="gaming-today-header-console">Consola</th>
                    <th className="px-2 py-1.5 text-left text-[10px] font-medium" data-testid="gaming-today-header-game">Jogo</th>
                    <th className="px-2 py-1.5 text-left text-[10px] font-medium" data-testid="gaming-today-header-client">Cliente</th>
                    <th className="px-2 py-1.5 text-left text-[10px] font-medium" data-testid="gaming-today-header-duration">Duração</th>
                    <th className="px-2 py-1.5 text-right text-[10px] font-medium" data-testid="gaming-today-header-total">Valor</th>
                    <th className="px-2 py-1.5 text-center text-[10px] font-medium" data-testid="gaming-today-header-status">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {sessions.slice(0, 15).map(session => (
                    <tr key={session.id} className="hover:bg-slate-50/50">
                      <td className="px-2 py-1.5 font-mono text-[11px]" data-testid={`gaming-today-session-number-${session.id}`}>{session.session_number}</td>
                      <td className="px-2 py-1.5 text-[11px]" data-testid={`gaming-today-session-start-${session.id}`}>
                        {formatDateTime(session.start_at || session.started_at || session.created_at)}
                      </td>
                      <td className="px-2 py-1.5 text-[11px]" data-testid={`gaming-today-session-end-${session.id}`}>
                        {formatDateTime(session.end_at || session.expected_end_at || session.ends_at)}
                      </td>
                      <td className="px-2 py-1.5" data-testid={`gaming-today-session-console-${session.id}`}>
                        <Badge variant="outline" className="text-[10px]">#{session.console_number}</Badge>
                      </td>
                      <td className="px-2 py-1.5 text-[11px]" data-testid={`gaming-today-session-game-${session.id}`}>{session.service_name}</td>
                      <td className="px-2 py-1.5 text-[11px]" data-testid={`gaming-today-session-client-${session.id}`}>{session.customer_name || '-'}</td>
                      <td className="px-2 py-1.5 text-[11px]" data-testid={`gaming-today-session-duration-${session.id}`}>
                        {formatDuration(session.duration_seconds || ((session.duration_minutes || 0) * 60))}
                      </td>
                      <td className="px-2 py-1.5 text-right text-[11px] font-medium" data-testid={`gaming-today-session-total-${session.id}`}>{formatCurrency(session.total)}</td>
                      <td className="px-2 py-1.5 text-center" data-testid={`gaming-today-session-status-${session.id}`}>
                        {session.status === 'active' ? (
                          <Badge className="bg-green-100 text-[10px] text-green-700">Activa</Badge>
                        ) : (
                          <Badge variant="secondary" className="text-[10px]">Concluída</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* New Session Dialog */}
      <Dialog open={newSessionOpen} onOpenChange={setNewSessionOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gamepad2 className="h-5 w-5 text-purple-600" />
              Iniciar Sessão - Consola #{selectedConsole}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Game Selection */}
            <div className="space-y-2">
              <Label>Jogo/Serviço *</Label>
              <Select 
                value={selectedService?.id || ''} 
                onValueChange={(v) => setSelectedService(services.find(s => s.id === v))}
              >
                <SelectTrigger data-testid="gaming-service-select-trigger">
                  <SelectValue placeholder="Seleccionar jogo" />
                </SelectTrigger>
                <SelectContent>
                  {services.map(service => (
                    <SelectItem key={service.id} value={service.id}>
                      <div className="flex justify-between items-center w-full">
                        <span>{service.name}</span>
                        <span className="text-muted-foreground ml-2">
                          {formatCurrency(service.unit_price)} ({service.unit})
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Cliente */}
            <div className="space-y-2" data-testid="gaming-session-client-section">
              <Label>Cliente *</Label>
              <Input
                placeholder="Pesquisar cliente (nome / telefone / código)"
                value={clientSearch}
                onChange={(e) => setClientSearch(e.target.value)}
                data-testid="gaming-client-search-input"
              />
              <Select value={selectedClientId} onValueChange={handleSelectClient}>
                <SelectTrigger data-testid="gaming-client-select">
                  <SelectValue placeholder="Selecionar cliente" />
                </SelectTrigger>
                <SelectContent>
                  {filteredClients.slice(0, 250).map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {(client.code || client.client_code || 'CL-') + ' • ' + client.name + (client.phone ? ` (${client.phone})` : '')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setQuickClientOpen((prev) => !prev)}
                  data-testid="gaming-quick-create-client-toggle"
                >
                  <Plus className="h-3 w-3 mr-1" /> Criar Cliente
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleUseDefaultClient}
                  data-testid="gaming-default-client-button"
                >
                  Cliente Indiferenciado
                </Button>
              </div>

              {quickClientOpen && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 p-2 border rounded-md bg-slate-50" data-testid="gaming-quick-client-panel">
                  <Input
                    placeholder="Nome *"
                    value={quickClientForm.name}
                    onChange={(e) => setQuickClientForm((prev) => ({ ...prev, name: e.target.value }))}
                    data-testid="gaming-quick-client-name"
                  />
                  <Input
                    placeholder="Telefone *"
                    value={quickClientForm.phone}
                    onChange={(e) => setQuickClientForm((prev) => ({ ...prev, phone: e.target.value }))}
                    data-testid="gaming-quick-client-phone"
                  />
                  <Input
                    placeholder="NUIT (opcional)"
                    value={quickClientForm.nuit}
                    onChange={(e) => setQuickClientForm((prev) => ({ ...prev, nuit: e.target.value }))}
                    data-testid="gaming-quick-client-nuit"
                  />
                  <Input
                    placeholder="Morada (opcional)"
                    value={quickClientForm.address}
                    onChange={(e) => setQuickClientForm((prev) => ({ ...prev, address: e.target.value }))}
                    data-testid="gaming-quick-client-address"
                  />
                  <div className="md:col-span-2 flex justify-end">
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleQuickCreateClient}
                      disabled={creatingQuickClient}
                      data-testid="gaming-quick-client-create-button"
                    >
                      {creatingQuickClient ? 'A criar...' : 'Criar e Selecionar'}
                    </Button>
                  </div>
                </div>
              )}

              {selectedClient && (
                <p className="text-xs text-emerald-700" data-testid="gaming-selected-client-summary">
                  Selecionado: {selectedClient.name} ({selectedClient.phone || 'N/A'})
                </p>
              )}
            </div>

            {/* Quantity */}
            <div className="space-y-2">
              <Label>Quantidade de sessões</Label>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <span className="w-12 text-center font-bold">{quantity}</span>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setQuantity(quantity + 1)}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Payment Method (origem: Definições ERP / payment-accounts) */}
            <div className="space-y-2">
              <Label>Forma de Pagamento *</Label>
              <div className="flex gap-2 flex-wrap items-center">
                {/* Main payment buttons - Numerário, M-Pesa, E-MOLA */}
                {mainPayments.map(pm => (
                  <Button
                    key={pm.id}
                    size="sm"
                    variant={selectedPaymentAccountId === pm.id ? "default" : "outline"}
                    onClick={() => {
                      setSelectedPaymentAccountId(pm.id);
                      setOtherPaymentMethod('');
                    }}
                    data-testid={`gaming-payment-account-button-${pm.id}`}
                    className={selectedPaymentAccountId === pm.id ? "bg-purple-600 hover:bg-purple-700" : ""}
                  >
                    {(pm.payment_method_name || pm.name || '').toLowerCase().includes('cash') || (pm.payment_method_name || pm.name || '').toLowerCase().includes('numerário') ? 
                      <Banknote className="h-3 w-3 mr-1" /> : 
                     (pm.payment_method_name || pm.name || '').toLowerCase().includes('m-pesa') ? <Smartphone className="h-3 w-3 mr-1" /> :
                     (pm.payment_method_name || pm.name || '').toLowerCase().includes('e-mola') ? <Smartphone className="h-3 w-3 mr-1" /> :
                     <CreditCard className="h-3 w-3 mr-1" />}
                    {pm.payment_method_name || pm.name}
                  </Button>
                ))}
                
                {/* Dropdown for other payment methods */}
                {otherPayments.length > 0 && (
                  <Select 
                    value={otherPaymentMethod} 
                    onValueChange={(v) => {
                      setOtherPaymentMethod(v);
                      setSelectedPaymentAccountId(v);
                    }}
                  >
                    <SelectTrigger className={cn(
                      "w-[150px]",
                      otherPaymentMethod && "border-purple-400"
                    )}>
                      <SelectValue placeholder="Outros..." />
                    </SelectTrigger>
                    <SelectContent>
                      {otherPayments.map(pm => (
                        <SelectItem key={pm.id} value={pm.id}>
                          {(pm.payment_method_name || pm.name) + (pm.name && pm.payment_method_name && pm.name !== pm.payment_method_name ? ` • ${pm.name}` : '')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              {selectedPaymentAccount && (
                <p className="text-xs text-muted-foreground" data-testid="gaming-selected-payment-summary">
                  Conta: {selectedPaymentAccount.name} • Método: {selectedPaymentAccount.payment_method_name || selectedPaymentAccount.name}
                </p>
              )}
            </div>

            {/* Date/Time Override */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1 text-xs">
                <Clock className="h-3 w-3 text-amber-500" />
                Data/Hora (opcional)
              </Label>
              <Input 
                type="datetime-local"
                value={registeredAt}
                onChange={(e) => setRegisteredAt(e.target.value)}
                step={1}
                max={new Date().toISOString().slice(0, 19)}
                data-testid="gaming-session-start-datetime-input"
                className="border-amber-200"
              />
            </div>

            <Separator />

            {/* Total */}
            <div className="flex justify-between items-center text-lg font-bold">
              <span>Total</span>
              <span className="text-green-600">{formatCurrency(sessionTotal)}</span>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewSessionOpen(false)} data-testid="gaming-cancel-session-button">
              Cancelar
            </Button>
            <Button 
              className="bg-purple-600 hover:bg-purple-700"
              onClick={handleCreateSession}
              disabled={!selectedService || processing}
              data-testid="gaming-start-session-button"
            >
              {processing ? 'A iniciar...' : 'Iniciar Sessão'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Console Dialog */}
      <Dialog open={editConsoleOpen} onOpenChange={setEditConsoleOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-blue-600" />
              Editar Consola #{editingConsole?.console_number}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nome da Consola *</Label>
              <Input 
                placeholder="Ex: PlayStation 5, Xbox Series X"
                value={consoleName}
                onChange={(e) => setConsoleName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select value={consoleType} onValueChange={setConsoleType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="playstation">PlayStation</SelectItem>
                  <SelectItem value="xbox">Xbox</SelectItem>
                  <SelectItem value="nintendo">Nintendo</SelectItem>
                  <SelectItem value="pc">PC Gaming</SelectItem>
                  <SelectItem value="pandora">Pandora Box</SelectItem>
                  <SelectItem value="vr">VR/Realidade Virtual</SelectItem>
                  <SelectItem value="other">Outro</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Descrição (opcional)</Label>
              <Input 
                placeholder="Notas ou observações"
                value={consoleDescription}
                onChange={(e) => setConsoleDescription(e.target.value)}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditConsoleOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveConsole}>
              <Save className="h-4 w-4 mr-2" />
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Console Configuration Dialog */}
      <Dialog open={configDialogOpen} onOpenChange={setConfigDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Configurar Consolas
            </DialogTitle>
            <DialogDescription>
              Defina o número de consolas disponíveis
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-center gap-4">
              <Button 
                variant="outline" 
                size="icon"
                onClick={() => setTempConsoleCount(Math.max(1, tempConsoleCount - 1))}
              >
                <Minus className="h-4 w-4" />
              </Button>
              <div className="text-center">
                <span className="text-4xl font-bold text-purple-600">{tempConsoleCount}</span>
                <p className="text-sm text-muted-foreground">consolas</p>
              </div>
              <Button 
                variant="outline" 
                size="icon"
                onClick={() => setTempConsoleCount(Math.min(20, tempConsoleCount + 1))}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="flex gap-2 justify-center">
              {[4, 6, 8, 10, 12].map(n => (
                <Button 
                  key={n}
                  variant={tempConsoleCount === n ? "default" : "outline"} 
                  size="sm"
                  onClick={() => setTempConsoleCount(n)}
                  className={tempConsoleCount === n ? "bg-purple-600" : ""}
                >
                  {n}
                </Button>
              ))}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfigDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveConsoleCount} className="bg-purple-600 hover:bg-purple-700">
              Aplicar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </ModuleStateBoundary>
  );
}
